import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import type { PlanogramItemUpdateStatus } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { Color, SubgridTable } from "src/elements";
import { useTranslation } from "src/i18n";
import { SubgridCell } from "src/elements/SubgridTable/components/SubgridCell";
import { SubgridRowElement } from "src/elements/SubgridTable/components/SubgridRow";
import type { SubgridColumn } from "src/elements/SubgridTable/store/types";
import { dateIsoStringToLocaleString } from "src/utils";
import styled from "styled-components";
import { PageHeader } from "./PageHeader";
import type { StoreProductsMap, StoreDetail } from "./PageMerchflows_ExportPdf_StorePogId";
import { PageContainer, PdfFooter } from "./SingleBayPage";
import { createBaseProductStub } from "./singleBayUtils";

export interface ItemSummaryRow {
  id: string;
  productCode: string;
  name: string;
  date: string;
  index?: number;
}

type ProductWithUpdateStatus = BaseProduct & {
  updateStatus?: PlanogramItemUpdateStatus;
};

interface BuildSummaryRowsArgs {
  pog: Pog;
  storeDetail: StoreDetail | null;
  currentStoreCode?: string;
  storeProducts?: StoreProductsMap;
  excludedProductCodes?: string[];
}

export interface ItemSummaryPageRows {
  newItemRows: ItemSummaryRow[];
  deletedItemRows: ItemSummaryRow[];
  showNewEmptyRow: boolean;
  showDeletedEmptyRow: boolean;
  hasNoDeliveryHistory: boolean;
  hasComparisonError: boolean;
}

interface ItemSummaryPageProps {
  storeDetail: StoreDetail | null;
  page: ItemSummaryPageRows;
  pageNumber: number;
  totalPages: number;
  layoutIndex?: number;
}

// ITEM_SUMMARY_ROWS_PER_PAGE = 24 is the maximum number of product rows
// that can fit within a single column of the A4 layout's two-column summary table
// without causing vertical page overflow.
export const ITEM_SUMMARY_ROWS_PER_PAGE = 24;

const SummaryContent = styled.div`
  flex: 1;
  min-height: 0;
  padding: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  box-sizing: border-box;
`;

const NoDeliveryNotice = styled.div`
  grid-column: 1 / -1;
  font-size: 11px;
  font-style: italic;
  color: ${Color.textSecondary};
`;

const SummaryTableSection = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid ${Color.lightGrayHover};
  border-top: 1px solid ${Color.lightGrayHover};
`;

const SummaryTableTitle = styled.div`
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  border-right: 1px solid ${Color.lightGrayHover};
  border-bottom: 1px solid ${Color.lightGrayHover};
  background-color: ${Color.greenSmokeActive};
`;

const TableWrap = styled.div`
  min-height: 0;
  flex: 1;
  overflow: hidden;
`;

const WrappedText = styled.div`
  overflow-wrap: anywhere;
  white-space: normal;
  width: 100%;
`;

const summaryColumns: SubgridColumn<ItemSummaryRow>[] = [
  { id: "index", header: "", width: "30px", headerPadding: "0 8px" },
  { id: "productCode", header: "Product Code", width: "108px", headerPadding: "0 8px" },
  { id: "name", header: "Name", width: "minmax(0, 1fr)", headerPadding: "0 8px" },
];

const hasUpdateStatus = (product: BaseProduct): product is ProductWithUpdateStatus => {
  return "updateStatus" in product;
};

const hasInsertedUpdateStatus = (product: BaseProduct) => {
  if (!hasUpdateStatus(product)) return false;

  return (
    product.updateStatus?.operation === "INSERT" && product.updateStatus?.changeType === "INSERTED"
  );
};

const isNewProduct = (product: BaseProduct) => {
  return product.newItemFlag === true || hasInsertedUpdateStatus(product);
};

const shouldIncludeForStore = (storeCodes: string[] | undefined, currentStoreCode?: string) => {
  if (!currentStoreCode || !storeCodes) return true;

  return storeCodes.includes(currentStoreCode);
};

const getSummaryRowKey = (product: BaseProduct) => {
  return product.productCode || product.id || product.uniqueId;
};

const formatSummaryDate = (storeDetail: StoreDetail | null) => {
  return storeDetail?.go_live_date
    ? dateIsoStringToLocaleString(storeDetail.go_live_date, "ddd MMM DD YYYY", true)
    : "";
};

const normalizeProductCode = (productCode: string) => productCode.trim().toUpperCase();

const buildExcludedProductCodeSet = (productCodes: string[] | undefined) =>
  new Set((productCodes ?? []).map(normalizeProductCode));

const isExcludedProduct = (productCode: string, excludedProductCodes: Set<string>) =>
  excludedProductCodes.has(normalizeProductCode(productCode));

const buildRowsFromProducts = (products: BaseProduct[], date: string) => {
  const seenKeys = new Set<string>();
  const rows: ItemSummaryRow[] = [];

  for (const product of products) {
    const key = getSummaryRowKey(product);
    if (seenKeys.has(key)) continue;

    seenKeys.add(key);
    rows.push({
      id: product.id,
      productCode: product.productCode,
      name: product.name,
      date,
      index: rows.length + 1,
    });
  }

  return rows;
};

export const buildNewItemSummaryRows = ({
  pog,
  storeDetail,
  currentStoreCode,
  storeProducts,
  excludedProductCodes,
}: BuildSummaryRowsArgs) => {
  const date = formatSummaryDate(storeDetail);
  const excludedProductCodeSet = buildExcludedProductCodeSet(excludedProductCodes);

  if (storeProducts !== undefined) {
    const newProducts = pog.planogram
      .getAllPlanogramProducts()
      .filter((product) => product.productCode && !(product.productCode in storeProducts))
      .filter((product) => !isExcludedProduct(product.productCode, excludedProductCodeSet));
    return buildRowsFromProducts(newProducts, date);
  }

  const newProducts = pog.planogram
    .getAllPlanogramProducts()
    .filter(isNewProduct)
    .filter((product) => shouldIncludeForStore(product.newItemStoreCodes, currentStoreCode))
    .filter((product) => !isExcludedProduct(product.productCode, excludedProductCodeSet));

  return buildRowsFromProducts(newProducts, date);
};

export const buildDeletedItemSummaryRows = ({
  pog,
  storeDetail,
  currentStoreCode,
  storeProducts,
  excludedProductCodes,
}: BuildSummaryRowsArgs) => {
  const date = formatSummaryDate(storeDetail);
  const excludedProductCodeSet = buildExcludedProductCodeSet(excludedProductCodes);

  if (storeProducts !== undefined) {
    const planogramProductCodes = new Set(
      pog.planogram
        .getAllPlanogramProducts()
        .map((p) => p.productCode)
        .filter(Boolean),
    );

    const allProductsLookup = new Map<string, BaseProduct>();
    for (const p of pog.deletedItems) {
      if (p.productCode) {
        allProductsLookup.set(p.productCode, p);
      }
    }
    for (const p of pog.planogram.getAllPlanogramProducts()) {
      if (p.productCode) {
        allProductsLookup.set(p.productCode, p);
      }
    }

    const deletedProducts: BaseProduct[] = [];
    for (const code of Object.keys(storeProducts)) {
      const storeProduct = storeProducts[code];
      if (!storeProduct || isExcludedProduct(code, excludedProductCodeSet)) continue;
      const name = typeof storeProduct === "string" ? storeProduct : storeProduct.name;
      const isOos = typeof storeProduct === "string" ? false : !!storeProduct.is_oos;

      if (isOos) {
        continue;
      }

      if (!planogramProductCodes.has(code)) {
        const existingProduct = allProductsLookup.get(code);
        if (existingProduct) {
          deletedProducts.push(existingProduct);
        } else {
          deletedProducts.push(createBaseProductStub(code, name));
        }
      }
    }

    return buildRowsFromProducts(deletedProducts, date);
  }

  const deletedProducts = pog.deletedItems
    .filter((product) => shouldIncludeForStore(product.storeCodes, currentStoreCode))
    .filter((product) => !isExcludedProduct(product.productCode, excludedProductCodeSet));

  return buildRowsFromProducts(deletedProducts, date);
};

export const buildItemSummaryPages = ({
  pog,
  storeDetail,
  currentStoreCode,
  storeProducts,
  excludedProductCodes,
  hasNoDeliveryHistory = false,
  hasComparisonError = false,
  rowsPerPage = ITEM_SUMMARY_ROWS_PER_PAGE,
}: BuildSummaryRowsArgs & {
  rowsPerPage?: number;
  hasNoDeliveryHistory?: boolean;
  hasComparisonError?: boolean;
}) => {
  const storeCode = currentStoreCode || storeDetail?.store;
  const resolvedRowsPerPage = Math.max(1, Math.floor(rowsPerPage));
  const newItemRows = buildNewItemSummaryRows({
    pog,
    storeDetail,
    currentStoreCode: storeCode,
    storeProducts,
    excludedProductCodes,
  });
  const deletedItemRows = buildDeletedItemSummaryRows({
    pog,
    storeDetail,
    currentStoreCode: storeCode,
    storeProducts,
    excludedProductCodes,
  });
  const pageCount = Math.max(
    1,
    Math.ceil(newItemRows.length / resolvedRowsPerPage),
    Math.ceil(deletedItemRows.length / resolvedRowsPerPage),
  );

  return Array.from({ length: pageCount }, (_, pageIndex): ItemSummaryPageRows => {
    const pageStart = pageIndex * resolvedRowsPerPage;
    const pageEnd = pageStart + resolvedRowsPerPage;

    return {
      newItemRows: newItemRows.slice(pageStart, pageEnd),
      deletedItemRows: deletedItemRows.slice(pageStart, pageEnd),
      showNewEmptyRow: pageIndex === 0 && newItemRows.length === 0,
      showDeletedEmptyRow: pageIndex === 0 && deletedItemRows.length === 0,
      hasNoDeliveryHistory: pageIndex === 0 && hasNoDeliveryHistory,
      hasComparisonError: pageIndex === 0 && hasComparisonError,
    };
  });
};

const renderSummaryRow = (row: ItemSummaryRow) => {
  if (!row.productCode && !row.name) {
    return (
      <SubgridRowElement background={Color.greenSmoke} data-pdf-row="">
        <SubgridCell span={3} leftBorder>
          No items
        </SubgridCell>
      </SubgridRowElement>
    );
  }

  return (
    <SubgridRowElement background={Color.greenSmoke} data-pdf-row="">
      <SubgridCell leftBorder>{row.index}</SubgridCell>
      <SubgridCell>{row.productCode}</SubgridCell>
      <SubgridCell>
        <WrappedText>{row.name}</WrappedText>
      </SubgridCell>
    </SubgridRowElement>
  );
};

const emptyRow: ItemSummaryRow = {
  id: "",
  productCode: "",
  name: "",
  date: "",
};

const SummaryTable = ({
  title,
  rows,
  showEmptyRow,
  side,
}: {
  title: string;
  rows: ItemSummaryRow[];
  showEmptyRow: boolean;
  side: "new" | "deleted";
}) => {
  const tableRows = rows.length ? rows : showEmptyRow ? [emptyRow] : [];

  return (
    <SummaryTableSection>
      <SummaryTableTitle>{title}</SummaryTableTitle>
      <TableWrap data-pdf-summary-table={side}>
        <SubgridTable columns={summaryColumns} data={tableRows} rowRenderer={renderSummaryRow} />
      </TableWrap>
    </SummaryTableSection>
  );
};

export const ItemSummaryPage = ({
  storeDetail,
  page,
  pageNumber,
  totalPages,
  layoutIndex = 0,
}: ItemSummaryPageProps) => {
  const { t } = useTranslation("common");
  const { t: tDelivery } = useTranslation("planogramDelivery");
  return (
    <PageContainer data-pdf-summary-page={layoutIndex}>
      {storeDetail && <PageHeader storeDetail={storeDetail} />}
      <SummaryContent>
        {page.hasNoDeliveryHistory && (
          <NoDeliveryNotice>{tDelivery("itemSummary.noDeliveryHistory")}</NoDeliveryNotice>
        )}

        {page.hasComparisonError && (
          <NoDeliveryNotice>{tDelivery("itemSummary.comparisonError")}</NoDeliveryNotice>
        )}

        <SummaryTable
          title={t("newItemSummary")}
          rows={page.newItemRows}
          showEmptyRow={page.showNewEmptyRow}
          side="new"
        />
        <SummaryTable
          title={t("deletedItemSummary")}
          rows={page.deletedItemRows}
          showEmptyRow={page.showDeletedEmptyRow}
          side="deleted"
        />
      </SummaryContent>
      <PdfFooter
        storeDetail={storeDetail}
        segment=""
        pageNumber={pageNumber}
        totalPages={totalPages}
      />
    </PageContainer>
  );
};
